import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Bell, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import TemplatesSection from '../components/settings/TemplatesSection';

const ADMIN_ALERTS = [
  'failed_payment', 'trial_ending', 'quota_80', 'avv_update',
  'new_staff', 'new_waitlist_entry', 'slot_filled', 'daily_summary',
];
const PATIENT_REMINDERS = ['reminder_72h', 'reminder_48h', 'reminder_24h'];

export default function SettingsMessagesPage() {
  const { t } = useTranslation();
  const { practice, setPractice } = useAuth();
  const [adminPrefs, setAdminPrefs] = useState({});
  const [patientPrefs, setPatientPrefs] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!practice) return;
    const ap = practice.notification_prefs || {};
    const defaults = {};
    ADMIN_ALERTS.forEach((k) => { defaults[k] = ap[k] !== false; });
    setAdminPrefs(defaults);
    const pr = practice.patient_reminders || {};
    const pd = {};
    PATIENT_REMINDERS.forEach((k) => { pd[k] = pr[k] !== false; });
    setPatientPrefs(pd);
  }, [practice]);

  async function savePrefs() {
    setSaving(true);
    try {
      const r = await api.patch(`/api/practices/${practice._id}`, {
        notification_prefs: adminPrefs,
        patient_reminders: patientPrefs,
      });
      setPractice(r.data.practice);
      toast.success(t('settings.notificationsPrefs.saved'));
    } catch (_) { toast.error(t('errors.unknown')); }
    finally { setSaving(false); }
  }

  return (
    <div className="space-y-6">
      <section className="so-card p-5" data-testid="admin-alerts-section">
        <h2 className="text-sm font-semibold flex items-center gap-2 mb-1"><Bell size={14} className="text-accent"/>{t('settings.notificationsPrefs.adminTitle')}</h2>
        <p className="text-xs text-text-secondary mb-4">{t('settings.notificationsPrefs.adminSubtitle')}</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {ADMIN_ALERTS.map((k) => (
            <label key={k} className="flex items-start gap-3 p-3 rounded-md border border-border cursor-pointer hover:bg-background/40" data-testid={`alert-${k}`}>
              <input type="checkbox" className="mt-0.5" checked={!!adminPrefs[k]} onChange={(e) => setAdminPrefs({ ...adminPrefs, [k]: e.target.checked })}/>
              <div>
                <p className="text-sm font-medium">{t(`settings.notificationsPrefs.alerts.${k}.title`)}</p>
                <p className="text-[11px] text-text-secondary mt-0.5">{t(`settings.notificationsPrefs.alerts.${k}.hint`)}</p>
              </div>
            </label>
          ))}
        </div>
      </section>

      <section className="so-card p-5" data-testid="patient-reminders-section">
        <h2 className="text-sm font-semibold flex items-center gap-2 mb-1"><Clock size={14} className="text-accent"/>{t('settings.notificationsPrefs.patientTitle')}</h2>
        <p className="text-xs text-text-secondary mb-4">{t('settings.notificationsPrefs.patientSubtitle')}</p>
        <div className="grid sm:grid-cols-3 gap-3">
          {PATIENT_REMINDERS.map((k) => (
            <label key={k} className="flex items-start gap-3 p-3 rounded-md border border-border cursor-pointer hover:bg-background/40" data-testid={`reminder-${k}`}>
              <input type="checkbox" className="mt-0.5" checked={!!patientPrefs[k]} onChange={(e) => setPatientPrefs({ ...patientPrefs, [k]: e.target.checked })}/>
              <div>
                <p className="text-sm font-medium">{t(`settings.notificationsPrefs.reminders.${k}.title`)}</p>
                <p className="text-[11px] text-text-secondary mt-0.5">{t(`settings.notificationsPrefs.reminders.${k}.hint`)}</p>
              </div>
            </label>
          ))}
        </div>
        <div className="mt-4 flex justify-end">
          <button onClick={savePrefs} disabled={saving} className="so-btn so-btn-primary text-sm" data-testid="save-notification-prefs">{saving ? '…' : t('settings.notificationsPrefs.save')}</button>
        </div>
      </section>

      <TemplatesSection/>
    </div>
  );
}
